import { useQueries } from '@tanstack/react-query';

import type { AdminEstablishmentDto } from '@/api/supplier-client';
import { useSupplierClient } from '@/api/supplier-client-context';
import { supplierDetailKey } from '@/api/use-suppliers';

// =============================================================================
// use-signer-identity-resolver.ts — Résolution nominative des signataires CGU (§2.9).
// =============================================================================
// DÉCISION ARCH (schema.prisma : CguAcceptance.signer_contact_id = FK LOGIQUE, PAS de snapshot
// nom/email ; le contrat SupplierContactDirectoryPort exclut contactId) : le BC legal NE PEUT PAS
// résoudre nom/email côté serveur sous la Règle n°1 / invariant n°2. L'AFFICHAGE nominatif est donc
// une COMPOSITION FRONT (même pattern que signed-status) :
//
//   fan-out supplier-reference `getEstablishment(siret)` sur les SIRET DISTINCTS du journal →
//   agrégation des interlocuteurs → Map contactId → { nomComplet, email }.
//
// Le legal (journal, contactId opaque) et supplier-reference (identité nominative) ne se parlent
// JAMAIS côté serveur : la jointure contactId → identité se fait ICI, dans le front. Un contactId
// sans identité résolue (établissement absent / contact révoqué) reste NON résolu (le composant
// dégrade proprement vers l'identifiant opaque tronqué — jamais une valeur inventée).

/** Identité nominative résolue d'un signataire (PII admin-only, périmètre admin). */
export interface SignerIdentity {
  readonly nomComplet: string;
  readonly email: string;
}

/** Résultat du resolver : maps de résolution + états agrégés du fan-out. */
export interface SignerIdentityResolution {
  /** contactId → identité nominative. Absent = non résolu (dégradation front). */
  readonly byContactId: ReadonlyMap<string, SignerIdentity>;
  /** SIRET → raison sociale légale (pour le libellé du filtre fournisseur). Absent = non résolu. */
  readonly raisonSocialeBySiret: ReadonlyMap<string, string>;
  /** Au moins une lecture d'établissement est encore en cours. */
  readonly isPending: boolean;
}

/** Déduplique et ordonne (stabilité de `queryKey`) les SIRET non vides. */
function distinctSirets(sirets: readonly string[]): readonly string[] {
  return [...new Set(sirets.filter((s) => s.length > 0))].sort();
}

/**
 * Résout l'identité nominative (nom + email) des signataires par fan-out sur les SIRET DISTINCTS.
 * Réutilise la MÊME `queryKey` que la fiche A-04 (`supplierDetailKey`) : cache partagé TanStack,
 * aucune requête redondante si la fiche est déjà chargée ailleurs. Le fan-out est cross-fournisseur
 * (périmètre admin) : chaque établissement expose ses interlocuteurs, tous fusionnés dans une map
 * unique clée par contactId (les contactId sont uniques cross-établissement).
 */
export function useSignerIdentityResolver(
  sirets: readonly string[],
): SignerIdentityResolution {
  const client = useSupplierClient();
  const targets = distinctSirets(sirets);

  const results = useQueries({
    queries: targets.map((siret) => ({
      queryKey: supplierDetailKey(siret),
      queryFn: (): Promise<AdminEstablishmentDto> => client.getEstablishment(siret),
    })),
  });

  const byContactId = new Map<string, SignerIdentity>();
  const raisonSocialeBySiret = new Map<string, string>();
  for (const result of results) {
    const establishment = result.data;
    if (establishment === undefined) {
      continue;
    }
    if (establishment.raisonSocialeLegale !== null) {
      raisonSocialeBySiret.set(establishment.siret, establishment.raisonSocialeLegale);
    }
    for (const contact of establishment.interlocuteurs) {
      byContactId.set(contact.contactId, {
        nomComplet: contact.nomComplet,
        email: contact.email,
      });
    }
  }

  return {
    byContactId,
    raisonSocialeBySiret,
    isPending: results.some((r) => r.isPending),
  };
}
